/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module UpdateModule
 */

import {
    AfterViewInit,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';

import {
    IMustChecksInfo,
    IUpgradeStatusInfo,
    NodeType,
    UpgradeFsmState,
    UpgradeOpsType,
} from 'generated-types';

import { IAviDataGridConfig } from 'ng/modules/data-grid/components/avi-data-grid/avi-data-grid.types';
import { L10nService } from '@vmw/ngx-vip';
import { SchemaService } from 'ajs/modules/core/services/schema-service/schema.service';
import { Subscription } from 'rxjs';
import { UpgradeNodeCollection } from 'ajs/modules/upgrade/factories/upgrade-node-collection.factory';
import { UpgradeStateGroup } from 'ng/modules/update/update.types';
import * as globalL10n from 'global-l10n';

import './precheck-wizard-page.component.less';
import { UpdateStore } from '../../update.store';
import * as l10n from './precheck-wizard-page.l10n';

const { ...globalL10nKeys } = globalL10n;
const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const NodeTypeEnum = 'NodeType';

/**
 * Map of status labels to be set based on the pre-check state.
 */
const preCheckStatusMap = new Map([
    [UpgradeFsmState.UPGRADE_PRE_CHECK_STARTED, l10nKeys.preCheckPendingLabel],
    [UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS, l10nKeys.preCheckInProgressStatusLabel],
    [UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS, l10nKeys.preCheckSuccessStatusLabel],
    [UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR, l10nKeys.preCheckErrorStatusLabel],
    [UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING, l10nKeys.preCheckWarningStatusLabel],
]);

/**
 * Interface for representing status of upgradeNode.
 */
interface IUpgradeNodeStatus {
    name: string,
    status: UpgradeFsmState,
    type: NodeType,
}

/**
 * @description Wizard page Component to display Controller/SE-group
 * pre-check progress and transcript.
 * @author Dhanashree Tilak, Deepak Chaudhary
 */
@Component({
    selector: 'precheck-wizard-page',
    templateUrl: './precheck-wizard-page.component.html',
})
export class PreCheckWizardPageComponent implements OnDestroy, AfterViewInit, OnInit, OnChanges {
    /**
     * Hide upgrade action from modal.
     */
    @Input()
    public readonly = false;

    /**
     * Store ids of node for which pre-checks needs to be shown.
     */
    @Input()
    public selectedSegIds: string[];

    @Input()
    public upgradeNodeCollection: UpgradeNodeCollection;

    /**
     * Share pre-check state after completion.
     */
    @Output()
    public onPreCheckCompletion = new EventEmitter<UpgradeFsmState>();

    /**
     * Redirect to report page.
     */
    @Output()
    public onGoToReportPage = new EventEmitter<void>();

    /**
     * Upgrade Node version.
     */
    public version: string;

    /**
     * Status of each node selected for upgrade.
     * To present the node details in row of the datagrid
     */
    public upgradeNodeStatuses: IUpgradeNodeStatus[] = [];

    /**
     * Upgrade operation (upgrade/patch/rollback) label's l10nKey.
     */
    public upgradeOperationLabel: string;

    public readonly l10nKeys = l10nKeys;

    public readonly globalL10nKeys = globalL10nKeys;

    public preCheckList: IMustChecksInfo[];

    public preCheckState: UpgradeFsmState;

    public preCheckEndTime: string;

    public selectedNodesGridConfig: IAviDataGridConfig;

    /**
     * Transcript to be shown in readonly text-area.
     */
    public transcript: string;

    /**
     * Progress percentage of pre-check
     */
    public preCheckProgress: number;

    /**
     * Upgrade status hash.
     */
    public readonly upgradeStateHash = {
        ERROR: UpgradeStateGroup.GROUP_ERROR,
        SUCCESS: UpgradeStateGroup.GROUP_COMPLETED,
    };

    /**
     * Pre-check state hash.
     */
    public readonly upgradeFsmState = {
        ERROR: UpgradeFsmState.UPGRADE_PRE_CHECK_ERROR,
        IN_PROGRESS: UpgradeFsmState.UPGRADE_PRE_CHECK_IN_PROGRESS,
        SUCCESS: UpgradeFsmState.UPGRADE_PRE_CHECK_SUCCESS,
        WARNING: UpgradeFsmState.UPGRADE_PRE_CHECK_WARNING,
        STARTED: UpgradeFsmState.UPGRADE_PRE_CHECK_STARTED,
    };

    /**
     * Hash of UpgradeOpsType and its l10n keys.
     */
    private readonly upgradeOpsHash = {
        [UpgradeOpsType.PATCH]: this.l10nKeys.patchLabel,
        [UpgradeOpsType.ROLLBACK]: this.l10nKeys.rollbackLabel,
        [UpgradeOpsType.ROLLBACKPATCH]: this.l10nKeys.rollbackPatchLabel,
        [UpgradeOpsType.UPGRADE]: this.globalL10nKeys.upgradeLabel,
        [UpgradeOpsType.EVAL_PATCH]: this.l10nKeys.patchLabel,
        [UpgradeOpsType.EVAL_ROLLBACK]: this.l10nKeys.rollbackLabel,
        [UpgradeOpsType.EVAL_ROLLBACKPATCH]: this.l10nKeys.rollbackPatchLabel,
        [UpgradeOpsType.EVAL_UPGRADE]: this.globalL10nKeys.upgradeLabel,
    };

    /**
     * Subscription to get updates on pre-checks changes.
     */
    private preCheckLoadSubscription: Subscription;

    /**
     * Subscription to get updates on completion of Pre-checks.
     */
    private preChecksCompletedSubscription: Subscription;

    public constructor(
        private readonly l10nService: L10nService,
        private readonly schemaService: SchemaService,
        private readonly updateStore: UpdateStore,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    /** @override */
    public ngOnChanges(): void {
        if (this.upgradeNodeCollection) {
            this.handlePreChecksUpdate();
        }
    }

    /** @override */
    public ngOnInit(): void {
        this.preCheckLoadSubscription = this.updateStore.preChecksUpdate.subscribe(() => this.handlePreChecksUpdate());

        this.preChecksCompletedSubscription = this.updateStore
            .preChecksCompleted$.subscribe(
                (preChecksCompleted: boolean) => {
                    if (preChecksCompleted) {
                        this.onPreChecksCompletion();
                    }
                },
            );
    }

    /** @override */
    public ngAfterViewInit(): void {
        const {
            l10nService,
            schemaService,
        } = this;

        this.selectedNodesGridConfig = {
            fields: [
                {
                    label: l10nService.getMessage(l10nKeys.nodeNameLabel),
                    id: 'name',
                    transform: (row: IUpgradeNodeStatus) => row.name,
                },
                {
                    label: l10nService.getMessage(globalL10nKeys.typeLabel),
                    id: 'type',
                    transform: (row: IUpgradeNodeStatus) => schemaService.getEnumValueLabel(NodeTypeEnum, row.type),
                },
                {
                    label: l10nService.getMessage(globalL10nKeys.statusLabel),
                    id: 'status',
                    transform: (row: IUpgradeNodeStatus) => l10nService.getMessage(preCheckStatusMap.get(row.status)),
                },
            ],
            layout: {
                hideCheckboxes: true,
            },
        };
    }

    /**
     * @override
     * Unsubscribe from selectors.
     */
    public ngOnDestroy(): void {
        this.preCheckLoadSubscription.unsubscribe();
        this.preChecksCompletedSubscription.unsubscribe();
    }

    /**
     * Navigate to System Reports details page.
     */
    public redirectToReportsPage(event: Event): void {
        event.preventDefault();
        this.onGoToReportPage.emit();
    }

    /**
     * Get upgrade transcript as formated string.
     */
    public getTranscript(selectedNodeIds: string[]): string {
        const selectedNodes = new Set(selectedNodeIds);
        const allNodes = this.upgradeNodeCollection.getConfig();
        const filteredConfig = allNodes
            .filter((config: IUpgradeStatusInfo) => selectedNodes.has(config.uuid));

        this.preCheckProgress = filteredConfig.reduce((total, node) =>
            total + Number(node.upgrade_readiness.progress), 0) / filteredConfig.length;
        this.preCheckEndTime = filteredConfig.length ?
            filteredConfig[filteredConfig.length - 1].upgrade_readiness.end_time : '';

        return JSON.stringify(filteredConfig, null, 4);
    }

    public trackByIndex(index: number): number {
        return index;
    }

    /**
     * Called on pre-check operation completion. Call submit handler if pre-check succeed.
     */
    private onPreChecksCompletion(): void {
        this.onPreCheckCompletion.emit(this.preCheckState);
    }

    /**
     * Handler for pre-check updates. Set pre-check related values used in modal.
     */
    private handlePreChecksUpdate(): void {
        const selectedNodeIds = this.selectedSegIds?.length ?
            this.selectedSegIds : this.upgradeNodeCollection?.getItemIds();

        this.setPreCheckValues(selectedNodeIds);
    }

    /**
     * Set pre-check values of given nodes from collection.
     */
    private setPreCheckValues(selectedNodeIds: string[]): void {
        this.upgradeNodeStatuses = [];
        this.preCheckState = this.upgradeNodeCollection.getSelectedSegsOverallState(selectedNodeIds);
        this.onPreChecksCompletion();

        const controllerNode = this.upgradeNodeCollection.getControllerNode() ||
            this.upgradeNodeCollection.getItemById(selectedNodeIds[0]);

        selectedNodeIds.forEach((nodeId: string) => {
            const node = this.upgradeNodeCollection.getItemById(nodeId);

            this.setHeaderGridValues(node.getName(), node.nodeType, node.upgradeReadinessState);
        });

        const { upgrade_ops: upgradeOps = UpgradeOpsType.UPGRADE } = controllerNode.upgradeReadiness;

        this.version = controllerNode.getImageRefVersion() || controllerNode.getVersion(true);
        this.preCheckList = this.upgradeNodeCollection.getConsolidatedPreChecks(selectedNodeIds);
        this.transcript = this.getTranscript(selectedNodeIds);
        this.upgradeOperationLabel = this.upgradeOpsHash[upgradeOps as keyof typeof this.upgradeOpsHash];
    }

    /**
     * Set selected node details to show in the grid.
     */
    private setHeaderGridValues(
        nodeName: string,
        nodeType: NodeType,
        state: UpgradeFsmState,
    ): void {
        this.upgradeNodeStatuses.push({
            name: nodeName,
            type: nodeType,
            status: state,
        });
    }
}
